/**
 * Fila persistente de downloads (F6-T09, F12-T01).
 *
 * Persistente porque no iOS a PWA é suspensa sem aviso. Fechar e reabrir o app não
 * pode perder o que faltava baixar, e um item interrompido não pode ficar marcado
 * como concluído.
 */
import { db, type DownloadJob, type DownloadJobStatus } from '@/db/schema';

function jobId(signId: string): string {
  // Um job por sinal: evita enfileirar o mesmo vídeo duas vezes (F12-T20).
  return `dl-${signId}`;
}

export async function enqueue(
  signId: string,
  options: { originCategoryId?: string; at?: Date } = {},
): Promise<DownloadJob> {
  const { originCategoryId, at = new Date() } = options;

  return db.transaction('rw', db.downloadQueue, async () => {
    const existing = await db.downloadQueue.get(jobId(signId));

    // Já na fila ou baixando: não duplicar nem reiniciar o progresso.
    if (existing && (existing.status === 'queued' || existing.status === 'downloading')) {
      return existing;
    }

    const job: DownloadJob = {
      id: jobId(signId),
      signId,
      status: 'queued',
      queuedAt: at.toISOString(),
      bytesDownloaded: 0,
      attempts: existing?.attempts ?? 0,
      ...(originCategoryId ? { originCategoryId } : {}),
    };

    await db.downloadQueue.put(job);
    return job;
  });
}

export async function enqueueMany(
  signIds: string[],
  options: { originCategoryId?: string; at?: Date } = {},
): Promise<number> {
  let added = 0;
  for (const signId of signIds) {
    const before = await db.downloadQueue.get(jobId(signId));
    await enqueue(signId, options);
    if (!before || (before.status !== 'queued' && before.status !== 'downloading')) added += 1;
  }
  return added;
}

export async function getJob(signId: string): Promise<DownloadJob | undefined> {
  return db.downloadQueue.get(jobId(signId));
}

export async function updateJob(signId: string, patch: Partial<DownloadJob>): Promise<void> {
  await db.transaction('rw', db.downloadQueue, async () => {
    const current = await db.downloadQueue.get(jobId(signId));
    if (!current) return;
    await db.downloadQueue.put({ ...current, ...patch });
  });
}

export async function removeJob(signId: string): Promise<void> {
  await db.downloadQueue.delete(jobId(signId));
}

export async function listJobs(status?: DownloadJobStatus): Promise<DownloadJob[]> {
  const jobs = status
    ? await db.downloadQueue.where('status').equals(status).toArray()
    : await db.downloadQueue.toArray();

  // FIFO estável: a ordem de entrada é o que o usuário vê na tela.
  return jobs.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

/**
 * Próximos jobs elegíveis, respeitando o limite de concorrência.
 *
 * Ignora jobs em espera de backoff: um job devolvido à fila após falha de rede só
 * volta a ser elegível quando `retryAfter` passar.
 */
export async function claimNext(limit: number, now: Date = new Date()): Promise<DownloadJob[]> {
  const timestamp = now.toISOString();
  const queued = (await listJobs('queued')).filter(
    (job) => !job.retryAfter || job.retryAfter <= timestamp,
  );
  return queued.slice(0, Math.max(0, limit));
}

export async function countByStatus(): Promise<Record<DownloadJobStatus, number>> {
  const counts: Record<DownloadJobStatus, number> = {
    queued: 0,
    downloading: 0,
    paused: 0,
    error: 0,
    done: 0,
  };
  for (const job of await db.downloadQueue.toArray()) counts[job.status] += 1;
  return counts;
}

/**
 * Recuperação após reinício da aplicação (F20-T06).
 *
 * Um job marcado como `downloading` quando o app abre é resíduo de uma sessão morta:
 * ninguém está baixando aquilo. Volta para `queued` e o progresso parcial é zerado —
 * o arquivo temporário correspondente é descartado pela camada de arquivos.
 */
export async function recoverInterruptedJobs(): Promise<number> {
  return db.transaction('rw', db.downloadQueue, async () => {
    const stuck = await db.downloadQueue.where('status').equals('downloading').toArray();
    await db.downloadQueue.bulkPut(
      stuck.map((job) => ({ ...job, status: 'queued' as const, bytesDownloaded: 0 })),
    );
    return stuck.length;
  });
}

export async function clearFinishedJobs(): Promise<number> {
  return db.transaction('rw', db.downloadQueue, async () => {
    const done = await db.downloadQueue.where('status').equals('done').primaryKeys();
    await db.downloadQueue.bulkDelete(done);
    return done.length;
  });
}
